/** Normalize time string to H:i (e.g. "14:30") for backend validation */
const toHi = (time) => {
  if (!time || typeof time !== "string") return "";
  const t = time.trim();
  const match = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (match) {
    const h = match[1].padStart(2, "0");
    const m = match[2];
    return `${h}:${m}`;
  }
  return t;
};

const isValidBase64Image = (str) => {
  if (!str || typeof str !== "string") return false;
  return /^data:image\/(jpeg|jpg|png|webp|gif);base64,/.test(str);
};

const isImageUrl = (image) => {
  if (!image || typeof image !== "string") return false;
  return /^https?:\/\//.test(image);
};

const processImage = (image) => {
  if (!image) return null;
    if (isImageUrl(image)) {
    return image;
  }
  
  // If it's already base64, return as-is
  if (isValidBase64Image(image)) {
    return image;
  }
  
  // If it's a string but not base64 format, try to convert it
  if (typeof image === "string" && image.length > 0) {
    return `data:image/jpeg;base64,${image}`;
  }
  
  return null;
};

const formatDateForInput = (dateString) => {
  if (!dateString) return "";
  
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return "";
    }
    
    // Format to YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.warn("Error formatting date:", error);
    return "";
  }
};

export const transformEventPayload = (formData) => {
  const startDate = formData.eventStartDate || formData.eventDate || "";
  const endDate = formData.eventEndDate || formData.eventStartDate || formData.eventDate || "";
  const startTime = formData.eventStartTime || formData.eventTime || "";
  const endTime = formData.eventEndTime || formData.eventStartTime || formData.eventTime || "";
  return {
    organizer_slug: formData.organizer?.slug || null,
    event_title: formData.eventName || "",
    event_type: formData.eventType || [],
    event_start_date: startDate,
    event_end_date: endDate,
    event_start_time: startTime,
    event_end_time: endTime,
    venue_name: formData.venueName || "",
    venue_street_address: formData.venueStreetAddress || "",
    country: formData.country || "",
    city: formData.city || "",
    venue_google_location: formData.venueGoogleLocation || "",
    thumbnail: processImage(formData.thumbnail),
    featured_images: (formData.featuredImages || [])
      .map((img) => processImage(img))
      .filter(Boolean),

    // Event details
    about_event: formData.aboutEvent || "",
    performers: (formData.performers || []).map((p) =>
      typeof p === "string" ? { name: p, image: null } : { name: p?.name || "", image: processImage(p?.image) }
    ),
    sponsors: (formData.sponsors || [])
      .map((sponsor) => ({
        name: sponsor.name || "",
        logo: processImage(sponsor.logo),
      }))
      .filter((sponsor) => sponsor.logo !== null),
    agenda: formData.agenda || [],

    // Tickets
    overall_slots: formData.overallSlots
      ? parseInt(formData.overallSlots)
      : null,
    ticket_types: (formData.ticketTypes || []).map((ticket) => ({
      name: ticket.name,
      price: parseFloat(ticket.price),
      currency: ticket.currency || "GHS",
      slots_allocated: parseInt(ticket.slotsAllocated),
      description: ticket.description,
      whats_included: Array.isArray(ticket.whatsIncluded)
        ? ticket.whatsIncluded
        : [],
    })),
  };
};

export const transformEventToFormData = (eventData, organizer = null) => {
  if (!eventData) return null;

  return {
    organizer: organizer || eventData.organizer || null,
    // Event basic information
    eventName: eventData.event_title || "",
    eventType: Array.isArray(eventData.event_type)
      ? eventData.event_type
      : eventData.event_type
      ? [eventData.event_type]
      : [],
    eventDate: formatDateForInput(eventData.event_date),
    eventStartDate: formatDateForInput(eventData.event_start_date || eventData.event_date),
    eventEndDate: formatDateForInput(eventData.event_end_date || eventData.event_date),
    eventTime: eventData.event_time || "",
    eventStartTime: toHi(eventData.event_start_time || eventData.event_time || ""),
    eventEndTime: toHi(eventData.event_end_time || eventData.event_time || ""),
    // Venue information
    venueName: eventData.venue_name || "",
    venueStreetAddress: eventData.venue_street_address || "",
    country: eventData.country || "",
    city: eventData.city || "",
    venueGoogleLocation: eventData.venue_google_location || "",
    // Media
    thumbnail: eventData.thumbnail || null,
    featuredImages: Array.isArray(eventData.featured_images)
      ? eventData.featured_images
      : [],
    // Event details
    aboutEvent: eventData.about_event || "",
    performers: Array.isArray(eventData.performers)
      ? eventData.performers.map((p) =>
          typeof p === "string" ? { name: p, image: null } : { name: p?.name || "", image: p?.image || null }
        )
      : [],
    sponsors: Array.isArray(eventData.sponsors)
      ? eventData.sponsors.map((sponsor) => ({
          name: sponsor.name || "",
          logo: sponsor.logo || null,
        }))
      : [],
    agenda: Array.isArray(eventData.agenda)
      ? eventData.agenda.map((item) => ({
          title: item.title || "",
          time: item.time || "",
          description: item.description || "",
        }))
      : [],
    // Tickets
    overallSlots: eventData.overall_slots
      ? String(eventData.overall_slots)
      : "",
    ticketTypes: Array.isArray(eventData.ticket_types)
      ? eventData.ticket_types.map((ticket) => ({
          name: ticket.name || "",
          price: ticket.price ? String(ticket.price) : "",
          currency: ticket.currency || "GHS", // Include currency, default to GHS for backward compatibility
          slotsAllocated: ticket.slots_allocated
            ? String(ticket.slots_allocated)
            : "",
          description: ticket.description || "",
          whatsIncluded: Array.isArray(ticket.whats_included)
            ? ticket.whats_included
            : [],
        }))
      : [],
  };
};
